/**
 * Sub-component (not a routed page): edit form for one search result
 * (SearchSingleResultExtractedData) within the search training data ground truth.
 * Renders InputFields for title, description, size, price, image URL (with preview),
 * and product URL (with open-in-new-tab link), writing changes back into the parent's
 * SearchResultsExtractedData via the use-immer Updater at this result's index.
 * Composed by SearchExtractedDataForm; makes no API calls.
 */
import React from "react";
import InputField, { FieldConfig } from "@/components/InputField";
import { SearchResultsExtractedData, SearchSingleResultExtractedData } from "@/types/trainingData";
import { Updater } from "use-immer";
import { Trash2, Link2 } from "lucide-react";

interface SearchSingleResultFormProps {
    index: number;
    result: SearchSingleResultExtractedData;
    setSearchResultsExtractedData: Updater<SearchResultsExtractedData | null>;
    onDelete: () => void;
    readonly?: boolean;
}

const SearchSingleResultForm: React.FC<SearchSingleResultFormProps> = ({
    index,
    result,
    setSearchResultsExtractedData,
    onDelete,
    readonly = false,
}) => {
    const updateField = <K extends keyof SearchSingleResultExtractedData>(
        field: K,
        value: SearchSingleResultExtractedData[K]
    ) => {
        setSearchResultsExtractedData((draft) => {
            if (draft && draft.results[index]) {
                draft.results[index][field] = value;
            }
        });
    };

    const titleConfig: FieldConfig = {
        key: `title_${index}`,
        label: "Title",
        type: "text",
        readonly: readonly,
        fieldState: result.title,
        setFieldState: (val: string) => updateField("title", val),
        placeholder: "enter product title",
    };

    const descriptionConfig: FieldConfig = {
        key: `description_${index}`,
        label: "Description",
        type: "textarea",
        readonly: readonly,
        fieldState: result.description ?? "",
        setFieldState: (val: string) => updateField("description", val),
        placeholder: "enter product description",
    };

    const sizeConfig: FieldConfig = {
        key: `size_${index}`,
        label: "Size",
        type: "text",
        readonly: readonly,
        fieldState: result.size ?? "",
        setFieldState: (val: string) => updateField("size", val),
        placeholder: "enter product size",
    };

    const priceConfig: FieldConfig = {
        key: `price_${index}`,
        label: "Price",
        type: "number",
        readonly: readonly,
        fieldState: result.price === "0" || !result.price ? null : parseFloat(result.price),
        setFieldState: (val: number | null) => {
            if (val !== null) {
                // Format to 2 decimal places when setting
                updateField("price", val.toFixed(2));
            }
        },
        min: 0,
        max: 999999,
        useTwoDecimalPlaces: true,
    };

    const imageUrlConfig: FieldConfig = {
        key: `imageUrl_${index}`,
        label: "Image URL",
        type: "text",
        readonly: readonly,
        fieldState: result.imageUrl,
        setFieldState: (val: string) => updateField("imageUrl", val),
        placeholder: "enter image url",
    };

    const productUrlConfig: FieldConfig = {
        key: `productUrl_${index}`,
        label: "Product URL",
        type: "text",
        readonly: readonly,
        fieldState: result.productUrl,
        setFieldState: (val: string) => updateField("productUrl", val),
        placeholder: "enter product url",
    };

    return (
        <div className="flex flex-col">
            {!readonly && (
                <div className="flex justify-end">
                    <button onClick={onDelete} className="text-red-600 hover:text-red-800">
                        <Trash2 className="w-5 h-5" />
                    </button>
                </div>
            )}
            <div className="flex flex-col gap-2">
                <InputField fieldConfig={titleConfig} />
                <InputField fieldConfig={descriptionConfig} height="h-24" />
                <InputField fieldConfig={sizeConfig} />
                <InputField fieldConfig={priceConfig} />
                <div className="flex gap-2 items-center">
                    <div className="flex-1">
                        <InputField fieldConfig={imageUrlConfig} />
                    </div>
                    {result.imageUrl && (
                        <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                        >
                            <img
                                src={result.imageUrl}
                                alt="Preview"
                                className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-75"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                }}
                            />
                        </a>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    <div className="flex-1">
                        <InputField fieldConfig={productUrlConfig} />
                    </div>
                    {result.productUrl && (
                        <a
                            href={result.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 h-full text-black hover:bg-blue-50 rounded"
                            title="Open in new tab"
                        >
                            <Link2 className="w-5 h-5" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchSingleResultForm;
